import React, { FC } from 'react'
import { useQueryClient } from 'react-query'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppBinWarningIcon } from 'components/Common/AppSvgIcons/AppBinWarningIcon'
import {
  useDeleteUserFilesMutation,
  UserFile,
  UserTask,
  useUpdateUserTaskMutation,
} from '__generated__/api-types-and-hooks'
import { getTenantId } from 'utils/getTenantId'
import { AppTaskFileCard } from '../AppTask/AppTaskFileCard/AppTaskFileCard.component'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'

interface IAppDeleteFile {
  task?: UserTask
  file: UserFile
  onCancel: () => void
  onDelete?: (success: boolean) => void
}

export const AppDeleteFile: FC<IAppDeleteFile> = ({ task, file, onCancel, onDelete }) => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  const {
    mutate: saveTaskFiles,
    isLoading: isFilesSaveLoading,
    isSuccess: isFilesSaveSuccess,
  } = useUpdateUserTaskMutation()

  const { mutate: deleteFile, isSuccess: fileDeleteSuccess } = useDeleteUserFilesMutation()

  const handleSaveUploadedFiles = async () => {
    try {
      // remove file url from tasks and upsateuser task
      saveTaskFiles(
        {
          input: {
            tenantId,
            userId: task?.userId ?? '',
            taskId: task?.taskId ?? '',
            files: [...(task?.files ?? []).filter((tf) => tf.id !== file.id)],
          },
        },
        {
          onSuccess: () => {
            // Invalidate queries to ensure fresh data
            queryClient.invalidateQueries('getUserTasks')
            queryClient.invalidateQueries('getAdvisorAssignedTasks')
            queryClient.invalidateQueries('getBusinessGoal')
            queryClient.invalidateQueries('getUserTask')
            queryClient.invalidateQueries('searchUserTasks')
          },
        }
      )

      // delete file
      deleteFile(
        {
          input: {
            recordIds: [file.id ?? ''],
          },
        },
        {
          onSuccess: () => {
            onDelete?.(true)
          },
          onError: () => {
            onDelete?.(true)
          },
        }
      )
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  return (
    <div>
      <AppModalBox
        classes="p-6 pt-0 z-50 mt-8"
        height="h-auto"
        width="w-[380px]"
        onClose={() => {
          if (!isFilesSaveLoading) {
            onCancel()
            if (isFilesSaveSuccess && onDelete) onDelete(true)
          }
        }}
      >
        <div className="flex flex-col items-center ">
          <div className="bg-app-red-25 rounded-xl p-2">
            <AppBinWarningIcon />
          </div>

          <h2 className=" font-inter text-xl font-medium text-app-black-100 mt-8">
            {`${isFilesSaveSuccess ? 'Deleted Upload' : 'Delete Upload?'}`}
          </h2>
          {!isFilesSaveSuccess && (
            <div className="flex flex-col mt-2">
              <span className="text-app-grey-70 text-sm text-center font-inter">
                Are you sure you want to delete this file?
              </span>
              <span className="text-app-grey-70 text-sm text-center font-inter">
                The action is irreversible.
              </span>
            </div>
          )}
        </div>
        <div className="mt-8">
          <AppTaskFileCard key={file.id} file={file} success={fileDeleteSuccess} />
        </div>

        <div className="flex flex-col w-full gap-2 mt-8">
          {!isFilesSaveSuccess && (
            <div className="relative">
              <AppButton
                label="Delete File"
                variant="alert"
                size="md"
                onClick={(event) => {
                  event && event.stopPropagation()
                  handleSaveUploadedFiles()
                }}
                disabled={isFilesSaveLoading}
              />
              {isFilesSaveLoading && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-2">
                  <GradientCircleNotchIcon width="16" height="16" />
                  <span className="text-app-white-100 font-inter text-sm">Delete File</span>
                </div>
              )}
            </div>
          )}
        </div>
      </AppModalBox>
    </div>
  )
}
